import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useContatosGlobal } from '@/api/queries/contatos';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Phone, Mail, MessageCircle, Linkedin, Instagram, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/cn';

function cleanPhone(numero: string): string {
  return numero.replace(/\D/g, '');
}
function whatsappUrl(numero: string): string {
  const clean = cleanPhone(numero);
  const withCountry = clean.startsWith('55') ? clean : `55${clean}`;
  return `https://wa.me/${withCountry}`;
}
function ensureUrl(value: string): string {
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

const tempColor: Record<string, string> = {
  frio: 'bg-slate-200 text-slate-700',
  morno: 'bg-amber-200 text-amber-900',
  quente: 'bg-red-200 text-red-900',
};

export function ContatosPage() {
  const [q, setQ] = useState('');
  const [onlyDecisor, setOnlyDecisor] = useState(false);
  const { data, isLoading } = useContatosGlobal({ q: q || undefined, eh_decisor: onlyDecisor, limit: 500 });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          Contatos <span className="text-base text-muted-foreground">({data?.length ?? 0})</span>
        </h1>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Buscar nome, empresa, cargo..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md"
        />
        <label className="inline-flex items-center gap-2 text-sm border rounded-md px-3 cursor-pointer hover:bg-muted">
          <input
            type="checkbox"
            checked={onlyDecisor}
            onChange={(e) => setOnlyDecisor(e.target.checked)}
          />
          Só decisores
        </label>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.length === 0 ? (
        <p className="text-muted-foreground">Nenhum contato encontrado.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data.map((c) => (
            <Card key={c.id} className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium truncate">{c.nome}</h4>
                    {c.eh_decisor && <Badge>decisor</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{c.cargo_titulo_livre ?? c.papel}</p>
                  <Link
                    to={`/empresas/${c.empresa_id}`}
                    className="text-sm hover:underline truncate block"
                  >
                    {c.empresa_nome}
                  </Link>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    {c.empresa_cidade && <span>{c.empresa_cidade}</span>}
                    {c.empresa_temperatura && (
                      <Badge
                        className={cn(tempColor[c.empresa_temperatura], 'text-xs px-1.5 py-0')}
                      >
                        {c.empresa_temperatura}
                      </Badge>
                    )}
                  </p>
                </div>
              </div>

              {c.telefones.length > 0 && (
                <div className="flex flex-wrap gap-1 text-xs">
                  {c.telefones.slice(0, 2).map((t, i) => (
                    <div key={i} className="inline-flex items-center gap-1">
                      <a href={`tel:${cleanPhone(t.numero)}`} className="hover:underline inline-flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {t.numero}
                      </a>
                      <a
                        href={whatsappUrl(t.numero)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:bg-green-100 rounded p-0.5"
                      >
                        <MessageCircle className="h-3 w-3" />
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {c.emails.length > 0 && (
                <div className="text-xs">
                  <a
                    href={`mailto:${c.emails[0]!.endereco}`}
                    className="hover:underline inline-flex items-center gap-1 truncate"
                  >
                    <Mail className="h-3 w-3" /> {c.emails[0]!.endereco}
                  </a>
                </div>
              )}

              {(c.linkedin_url || c.instagram_url) && (
                <div className="flex gap-1">
                  {c.linkedin_url && (
                    <a
                      href={ensureUrl(c.linkedin_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs hover:bg-muted"
                    >
                      <Linkedin className="h-3 w-3" />
                      <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                    </a>
                  )}
                  {c.instagram_url && (
                    <a
                      href={ensureUrl(c.instagram_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs hover:bg-muted"
                    >
                      <Instagram className="h-3 w-3" />
                      <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                    </a>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
